import { useState, type ReactNode } from 'react'
import { Ban, Check, ChevronDown, ChevronUp } from 'lucide-react'
import type { Topic } from '@/api/topics/types'
import { Badge, StatusBadge } from '@/components/Badge'
import { TopicDetail } from '@/features/topics/TopicDetail'
import { formatDateTime, formatNumber } from '@/lib/format'

interface TopicRowProps {
  readonly topic: Topic
  readonly isPending: boolean
  readonly showProducttype?: boolean
  readonly onApprove: (topicId: number) => void
  readonly onIgnore: (topicId: number) => void
  readonly onReset: (topicId: number) => void
}

export function TopicRow({
  topic,
  isPending,
  showProducttype = true,
  onApprove,
  onIgnore,
  onReset,
}: TopicRowProps): ReactNode {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {topic.ranking && (
              <span className="shrink-0 rounded bg-slate-900 px-2 py-0.5 text-xs font-semibold tabular-nums text-white dark:bg-slate-100 dark:text-slate-900">
                #{topic.ranking.position}
              </span>
            )}
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {topic.topic}
            </h3>
            <StatusBadge status={topic.status} />
            <Badge>{topic.categoryLabel}</Badge>
          </div>

          {(showProducttype || topic.marginGroup !== null) && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {showProducttype && (
                <>
                  {topic.producttypeName ?? topic.producttypeKey}
                  {topic.producttypeName !== null && (
                    <span className="text-slate-400 dark:text-slate-500">
                      {' '}
                      ({topic.producttypeKey})
                    </span>
                  )}
                </>
              )}
              {showProducttype && topic.marginGroup && <> · </>}
              {topic.marginGroup && <>Marge {topic.marginGroup.label}</>}
            </p>
          )}
        </div>

        <dl className="flex shrink-0 gap-5 text-right">
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400">Max. Volumen</dt>
            <dd className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(topic.searchVolumeMax)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400">Summe</dt>
            <dd className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(topic.searchVolumeSum)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400">Keywords</dt>
            <dd className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(topic.keywordCount)}
            </dd>
          </div>
        </dl>
      </div>

      {topic.niche && (
        <p className="mt-3 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950 dark:text-emerald-100">
          Nische verknüpft: <strong>{topic.niche.name}</strong>
          {topic.statusChangedAt !== null && (
            <span className="opacity-75"> · {formatDateTime(topic.statusChangedAt)}</span>
          )}
          {topic.statusChangedBy !== null && (
            <span className="opacity-75"> · {topic.statusChangedBy}</span>
          )}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {topic.status === 'Open' ? (
          <>
            <button
              type="button"
              disabled={isPending}
              onClick={() => {
                onApprove(topic.id)
              }}
              className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-1.5">
                <Check aria-hidden="true" size={15} strokeWidth={2.2} />
                Freigeben
              </span>
            </button>
            <button
              type="button"
              disabled={isPending}
              onClick={() => {
                onIgnore(topic.id)
              }}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <span className="inline-flex items-center gap-1.5">
                <Ban aria-hidden="true" size={15} strokeWidth={1.9} />
                Ignorieren
              </span>
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={isPending}
            onClick={() => {
              onReset(topic.id)
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Zurücksetzen
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            setExpanded((value) => !value)
          }}
          className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-500 underline-offset-2 transition hover:text-slate-900 hover:underline dark:text-slate-400 dark:hover:text-slate-100"
        >
          {expanded ? (
            <ChevronUp aria-hidden="true" size={15} />
          ) : (
            <ChevronDown aria-hidden="true" size={15} />
          )}
          {expanded ? 'Details ausblenden' : 'Details anzeigen'}
        </button>
      </div>

      {expanded && <TopicDetail topic={topic} />}
    </div>
  )
}
